import React, { useState, useEffect } from 'react';
import './ServicePage.css';
import '@fortawesome/fontawesome-free/css/all.min.css';

const ImageSlider = ({ images }) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) =>
        prevIndex === images.length - 1 ? 0 : prevIndex + 1
      );
    }, 3000);
    return () => clearInterval(interval);
  }, [images.length]);

  return (
    <div className="service-page-image-slider">
      <img
        src={images[currentIndex]}
        alt={`Slide ${currentIndex}`}
        className="service-page-slider-image"
      />
    </div>
  );
};

const FeatureItem = ({ icon, title, description }) => (
  <div className="service-page-feature-item">
    <i className={`fas ${icon}`} />
    <h4>{title}</h4>
    <p>{description}</p>
  </div>
);

const HighlightBox = ({ title, items }) => (
  <div className="service-page-highlight-box">
    <h4>{title}</h4>
    <ul>
      {items.map((item, index) => (
        <li key={index}>{item}</li>
      ))}
    </ul>
  </div>
);

const ServicePage = () => {
  const services = [
    {
      icon: 'fab fa-android',
      title: 'Android Apps',
      description:
        'Build fast, secure, and reliable Android apps that deliver superior performance.',
      highlightItems: [
        'High performance and fast-loading apps',
        'Top-notch security features to protect user data',
        'Custom solutions tailored to your business needs',
        'Scalable for future growth and updates',
      ],
    },
    {
      icon: 'fab fa-apple',
      title: 'iOS Apps',
      description:
        'Deliver seamless, secure, and high-performance iOS apps for an exceptional user experience.',
      highlightItems: [
        'Optimized for iOS with smooth performance',
        'Secure and compliant with industry standards',
        'Easy integration with Apple’s ecosystem',
        'User-friendly design for an intuitive experience',
      ],
    },
    {
      icon: 'fas fa-comments',
      title: 'Mobile App Consultation',
      description:
        'Get expert guidance to transform your app idea into a functional product.',
      highlightItems: [
        'Clear roadmap for your app’s success',
        'Guidance on selecting the right technologies',
        'Support through every stage of development',
        'Insights into app market trends and user demands',
      ],
    },
  ];

  useEffect(() => {
    const canvas = document.getElementById("particleCanvas");
    const ctx = canvas.getContext("2d");
    const particles = [];
    const maxParticles = 150;
    const mouse = { x: null, y: null };

    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    const createParticle = () => {
      return {
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        vx: (Math.random() - 0.5) * 2,
        vy: (Math.random() - 0.5) * 2,
        radius: 2 + Math.random() * 4,
        color: `rgba(${Math.floor(Math.random() * 255)}, ${Math.floor(Math.random() * 255)}, ${Math.floor(Math.random() * 255)}, 0.6)`
      };
    };

    for (let i = 0; i < maxParticles; i++) {
      particles.push(createParticle());
    }

    const drawParticles = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particles.forEach((p) => {
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.radius, 0, Math.PI * 2);
        ctx.fillStyle = p.color;
        ctx.fill();
      });

      // Particle connections (updated for aesthetics)
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const distance = Math.sqrt(dx * dx + dy * dy);

          if (distance < 100) {
            ctx.beginPath();
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.strokeStyle = `rgba(255, 255, 255, ${1 - distance / 100})`;
            ctx.lineWidth = 0.5;
            ctx.stroke();
          }
        }
      }

      // Mouse interaction effect with particles
      if (mouse.x && mouse.y) {
        particles.forEach((p) => {
          const dx = p.x - mouse.x;
          const dy = p.y - mouse.y;
          const distance = Math.sqrt(dx * dx + dy * dy);

          if (distance < 150) {
            ctx.beginPath();
            ctx.moveTo(p.x, p.y);
            ctx.lineTo(mouse.x, mouse.y);
            ctx.strokeStyle = "rgba(0, 255, 0, 0.6)";
            ctx.lineWidth = 1;
            ctx.stroke();
          }
        });
      }
    };

    const updateParticles = () => {
      particles.forEach((p) => {
        p.x += p.vx;
        p.y += p.vy;

        if (p.x < 0 || p.x > canvas.width) p.vx *= -1;
        if (p.y < 0 || p.y > canvas.height) p.vy *= -1;
      });
    };

    const animate = () => {
      drawParticles();
      updateParticles();
      requestAnimationFrame(animate);
    };

    animate();

    // Update mouse position
    window.addEventListener("mousemove", (event) => {
      mouse.x = event.clientX;
      mouse.y = event.clientY;
    });

    window.addEventListener("mouseout", () => {
      mouse.x = null;
      mouse.y = null;
    });

    // Resize canvas on window resize
    window.addEventListener("resize", () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    });
  }, []);

  return (
    <main>
      <header className="service-page-header-section">
        <div className="service-page-header-content">
          <h1 className="service-page-header-title">
            Mobile and Windows Application Development
          </h1>
          <p className="service-page-header-description">
            We create high-quality, innovative applications using state-of-the-art
            technology. Our expertise spans Android, iOS, Windows, and cross-platform
            development, ensuring exceptional user experiences and seamless
            integration with your business needs.
          </p>
          <div className="service-page-features-card">
            {[
              { icon: 'fas fa-code', title: 'Expert Developers', description: 'Our team consists of experts in mobile technologies.' },
              { icon: 'fas fa-shield-alt', title: 'Security First', description: 'We ensure your data is always protected with the highest security standards.' },
              { icon: 'fas fa-user-friends', title: 'Customer Support', description: 'We provide round-the-clock customer support to help with your needs.' }
            ].map((feature, index) => (
              <FeatureItem key={index} {...feature} />
            ))}
          </div>
        </div>
      </header>

      {/* Particle Canvas */}
      <canvas id="particleCanvas" style={{ position: 'absolute', top: 0, left: 0, zIndex: -1 }}></canvas>

      <section className="service-page-tech-section">
        <h2>Technology We Use</h2>
        <p>We build your software with the latest technologies, ensuring optimal performance and scalability.</p>
        <div className="service-page-tech-icons">
          {[
            'react.png',
            'flutter_icon.png',
            'kotlin.png',
            'images__2_-removebg-preview.png',
            'sql.png'
          ].map((img, index) => (
            <img key={index} src={`/images/mobileapps/${img}`} alt={img.split('.')[0]} className="service-page-tech-icon" />
          ))}
        </div>
      </section>

      <section className="service-page-services-section">
        <div className="service-page-services-grid">
          {services.map((service, index) => (
            <div className="service-page-service-card" key={index}>
              <div className="service-page-service-card-header">
                <i className={`service-page-service-icon ${service.icon}`} />
                <h3>{service.title}</h3>
              </div>
              <p>{service.description}</p>
              <HighlightBox title="Key Highlights" items={service.highlightItems} />
            </div>
          ))}
        </div>
      </section>
    </main>
  );
};

export default ServicePage;
